/**
 * HP sloupce seznamu polygonů: součet z committed unified importu.
 * Fail-closed — bez committed importu s explicitním canonical.hp_count vrací null (ne 0).
 *
 * Zdroj: jen nahrané `building_import_canonical.hp_count` (ne ROP, ne spec, ne počet budov).
 * Override: jen když ten import HP existuje a user_edited_fields obsahuje hpCount.
 *
 * `connectedHpCount` = součet stejného HP jen u budov v **konečné fázi**
 * (`isBudovaLastPhase`: Zafouk bez DC / Svařený se DC / Bábovka Zafouk).
 * Zrušený se nepočítá.
 */

import {
  isBudovaLastPhase,
  PHASE_CODE,
  isSamePhase,
} from '../constants/object-workflow';


export const POLYGON_UNIFIED_HP_SUCCESS_STATUSES = ['committed'] as const;

export const POLYGON_UNIFIED_IMPORT_IDS_SQL = `
  SELECT DISTINCT b.polygon_id
  FROM building_import_batch b
  WHERE b.polygon_id = ANY($1::uuid[])
    AND b.status IN ('committed')
    AND EXISTS (
      SELECT 1
      FROM building_import_canonical c
      WHERE c.polygon_id = b.polygon_id
        AND c.hp_count IS NOT NULL
    )
`;

export const POLYGON_UNIFIED_HP_BUILDING_ROWS_SQL = `
  SELECT
    c.polygon_id,
    c.hp_count AS canonical_hp,
    oa.hp_count AS acq_hp,
    oa.user_edited_fields AS acq_edited,
    c.user_edited_fields AS can_edited
  FROM building_import_canonical c
  LEFT JOIN object_acquisition oa ON oa.object_id = c.object_id
  WHERE c.polygon_id = ANY($1::uuid[])
    AND c.hp_count IS NOT NULL
`;

/**
 * Unified HP řádky + status budovy + digitální cesta (pro connected HP).
 * `digitalni_cesta` NULL → 0 (bez DC; Zafouk je terminální u Připojená).
 */
export const POLYGON_UNIFIED_HP_CONNECTED_BUILDING_ROWS_SQL = `
  SELECT
    c.polygon_id,
    c.object_id,
    c.hp_count AS canonical_hp,
    oa.hp_count AS acq_hp,
    oa.user_edited_fields AS acq_edited,
    c.user_edited_fields AS can_edited,
    o.status AS object_status,
    COALESCE(pos.digitalni_cesta, 0) AS digitalni_cesta
  FROM building_import_canonical c
  LEFT JOIN object_acquisition oa ON oa.object_id = c.object_id
  INNER JOIN objects o
    ON o.id = c.object_id
   AND o.is_deleted = false
   AND o.type = 'BUDOVA'
  LEFT JOIN pripojovany_objekt_spec pos ON pos.object_id = c.object_id
  WHERE c.polygon_id = ANY($1::uuid[])
    AND c.hp_count IS NOT NULL
`;


export type PolygonUnifiedHpBuildingInput = {
  importHpCount?: unknown;
  userOverrideHpCount?: unknown;
  userOverrodeHp?: boolean;
};

export type PolygonUnifiedHpBuildingRow = {
  polygon_id?: unknown;
  object_id?: unknown;
  canonical_hp?: unknown;
  canonical_hp_rop?: unknown;
  acq_hp?: unknown;
  spec_hp?: unknown;
  acq_edited?: unknown;
  can_edited?: unknown;
};

export type PolygonUnifiedHpConnectedBuildingRow = PolygonUnifiedHpBuildingRow & {
  object_status?: unknown;
  digitalni_cesta?: unknown;
};

/** Budova v hotové připojovací fázi (ne Zrušený). */
export function isBudovaConnectedFinalPhase(
  status: string | null | undefined,
  digitalniCesta?: number | null,
): boolean {
  const code = (status ?? '').trim();
  if (!code) return false;
  if (isSamePhase(code, PHASE_CODE.BUDOVA_ODMITNUTY)) return false;
  if (code.startsWith('BUDOVA.1.')) return false;
  return isBudovaLastPhase(code, digitalniCesta);
}

/** HP z committed unified importu pro konkrétní budovy (fail-closed — bez nahraného hp_count řádek chybí). */
export const POLYGON_UNIFIED_HP_BY_OBJECT_IDS_SQL = `
  SELECT
    c.object_id,
    c.hp_count AS canonical_hp,
    oa.hp_count AS acq_hp,
    oa.user_edited_fields AS acq_edited,
    c.user_edited_fields AS can_edited
  FROM building_import_canonical c
  LEFT JOIN object_acquisition oa ON oa.object_id = c.object_id
  WHERE c.object_id = ANY($1::varchar[])
    AND c.hp_count IS NOT NULL
    AND EXISTS (
      SELECT 1
      FROM building_import_batch b
      WHERE b.polygon_id = c.polygon_id
        AND b.status IN ('committed')
    )
`;

export function parseNonNegativeInt(value: unknown): number | null {
  if (value == null || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isInteger(n) || n < 0) return null;
  return n;
}

export function userEditedHpFields(userEditedFields: unknown): boolean {
  if (!Array.isArray(userEditedFields)) return false;
  return userEditedFields.some((key) => key === 'hpCount');
}

/**
 * HP budovy: nahrané canonical.hp_count, případně vědomý přepis po tom importu.
 * Bez nahraného import HP vrací null — ruční akvizice / spec / ROP se nepočítají.
 */
export function resolveBuildingUnifiedHp(input: PolygonUnifiedHpBuildingInput): number | null {
  const imported = parseNonNegativeInt(input.importHpCount);
  if (imported == null) return null;
  if (input.userOverrodeHp) {
    const override = parseNonNegativeInt(input.userOverrideHpCount);
    if (override != null) return override;
  }
  return imported;
}

/**
 * Display priorita: user override (jen když unified import na budově nahrál HP)
 * → jinak HP z importu → jinak prázdno.
 */
export function resolvePolygonUnifiedHpCount(input: {
  unifiedImportSucceeded: boolean;
  buildings: readonly PolygonUnifiedHpBuildingInput[];
}): number | null {
  if (!input.unifiedImportSucceeded) return null;
  let sum = 0;
  let hasValue = false;
  for (const building of input.buildings) {
    const hp = resolveBuildingUnifiedHp(building);
    if (hp == null) continue;
    sum += hp;
    hasValue = true;
  }
  return hasValue ? sum : null;
}

export function buildingInputFromUnifiedHpRow(row: PolygonUnifiedHpBuildingRow): PolygonUnifiedHpBuildingInput {
  return {
    importHpCount: parseNonNegativeInt(row.canonical_hp),
    userOverrideHpCount: parseNonNegativeInt(row.acq_hp),
    userOverrodeHp: userEditedHpFields(row.acq_edited) || userEditedHpFields(row.can_edited),
  };
}

export function aggregatePolygonUnifiedHpById(
  importedPolygonIds: readonly string[],
  buildingRows: readonly PolygonUnifiedHpBuildingRow[],
): Map<string, number | null> {
  const imported = new Set(importedPolygonIds.filter((id) => id.trim().length > 0));
  const byPolygon = new Map<string, PolygonUnifiedHpBuildingInput[]>();
  for (const row of buildingRows) {
    const polygonId = typeof row.polygon_id === 'string' ? row.polygon_id.trim() : '';
    if (!polygonId || !imported.has(polygonId)) continue;
    const list = byPolygon.get(polygonId) ?? [];
    list.push(buildingInputFromUnifiedHpRow(row));
    byPolygon.set(polygonId, list);
  }

  const out = new Map<string, number | null>();
  for (const polygonId of imported) {
    out.set(
      polygonId,
      resolvePolygonUnifiedHpCount({
        unifiedImportSucceeded: true,
        buildings: byPolygon.get(polygonId) ?? [],
      }),
    );
  }
  return out;
}

/** HP budovy z unified import řádku; bez nahraného HP → null. */
export function resolveUnifiedHpFromRow(row: PolygonUnifiedHpBuildingRow): number | null {
  return resolveBuildingUnifiedHp(buildingInputFromUnifiedHpRow(row));
}

/**
 * Mapa object_id → HP z committed unified importu.
 * Budovy mimo výsledek SQL se do mapy nedávají (volající má fail-closed default null).
 */
export function aggregateUnifiedHpByObjectId(
  buildingRows: readonly PolygonUnifiedHpBuildingRow[],
): Map<string, number | null> {
  const out = new Map<string, number | null>();
  for (const row of buildingRows) {
    const objectId = typeof row.object_id === 'string' ? row.object_id.trim() : '';
    if (!objectId) continue;
    const hp = resolveUnifiedHpFromRow(row);
    if (hp == null) continue;
    out.set(objectId, hp);
  }
  return out;
}

function parseDigitalniCesta(value: unknown): number {
  if (value == null || value === '') return 0;
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n) || n < 0) return 0;
  return n;
}

/**
 * Součet unified HP budov v konečné fázi připojení (fail-closed stejně jako total HP).
 * Bez committed importu → mapa prázdná / null hodnoty jako u total.
 */
export function aggregatePolygonConnectedHpById(
  importedPolygonIds: readonly string[],
  buildingRows: readonly PolygonUnifiedHpConnectedBuildingRow[],
): Map<string, number | null> {
  const imported = new Set(importedPolygonIds.filter((id) => id.trim().length > 0));
  const byPolygon = new Map<string, number>();
  const hasAny = new Map<string, boolean>();

  for (const row of buildingRows) {
    const polygonId = typeof row.polygon_id === 'string' ? row.polygon_id.trim() : '';
    if (!polygonId || !imported.has(polygonId)) continue;
    hasAny.set(polygonId, true);
    const status = typeof row.object_status === 'string' ? row.object_status : String(row.object_status ?? '');
    const digitalniCesta = parseDigitalniCesta(row.digitalni_cesta);
    if (!isBudovaConnectedFinalPhase(status, digitalniCesta)) continue;
    const hp = resolveUnifiedHpFromRow(row);
    if (hp == null) continue;
    byPolygon.set(polygonId, (byPolygon.get(polygonId) ?? 0) + hp);
  }

  const out = new Map<string, number | null>();
  for (const polygonId of imported) {
    if (!hasAny.get(polygonId) && !byPolygon.has(polygonId)) {
      // Import exists but no building rows matched — still show 0 connected (total may be >0).
      out.set(polygonId, 0);
      continue;
    }
    out.set(polygonId, byPolygon.get(polygonId) ?? 0);
  }
  return out;
}
